import { useEffect, useState } from 'react';

interface DebugPanelProps {
  // Props can be empty for now
}

/**
 * A hidden debugging panel that can be activated by pressing a keyboard shortcut
 * Displays information about the cache busting system
 */
export const DebugPanel = ({}: DebugPanelProps): JSX.Element | null => {
  const [visible, setVisible] = useState(false);
  const [versionInfo, setVersionInfo] = useState<any>({
    server: 'loading...',
    client: {
      version: localStorage.getItem('appVersion') || 'none',
      buildId: localStorage.getItem('appBuildId') || 'none',
      justUpdated: localStorage.getItem('justUpdated') || 'false'
    },
    cacheBusterMounted: window.CACHE_BUSTER_MOUNTED || false
  });

  // Function to check version info
  const checkVersionInfo = async () => {
    try {
      // Add cache-busting parameter
      const timestamp = new Date().getTime();
      const response = await fetch(`/version.json?t=${timestamp}`);

      if (response.ok) {
        const serverVersion = await response.json();

        setVersionInfo({
          server: serverVersion,
          client: {
            version: localStorage.getItem('appVersion') || 'none',
            buildId: localStorage.getItem('appBuildId') || 'none',
            justUpdated: localStorage.getItem('justUpdated') || 'false'
          },
          cacheBusterMounted: window.CACHE_BUSTER_MOUNTED || false,
          lastChecked: new Date().toISOString()
        });

        return serverVersion;
      } else {
        console.error('Failed to fetch version info:', response.statusText);
        return null;
      }
    } catch (error) {
      console.error('Error fetching version info:', error);
      return null;
    }
  };

  // Toggle panel visibility with keyboard shortcut (Alt+Shift+D)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.shiftKey && event.key === 'D') {
        setVisible((prev) => !prev);
        checkVersionInfo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Refresh version info every 10 seconds when panel is visible
  useEffect(() => {
    if (!visible) return;

    checkVersionInfo();
    const interval = setInterval(checkVersionInfo, 10000);

    return () => {
      clearInterval(interval);
    };
  }, [visible]);

  if (!visible) return null;

  const match = versionInfo.client.version === versionInfo.server.version && versionInfo.client.buildId === versionInfo.server.buildId;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        right: 0,
        width: '400px',
        maxHeight: '80vh',
        overflowY: 'auto',
        background: '#f0f0f0',
        border: '1px solid #ccc',
        borderRadius: '4px 0 0 0',
        zIndex: 9999999,
        padding: '10px',
        fontSize: '12px',
        fontFamily: 'monospace',
        boxShadow: '0 0 10px rgba(0,0,0,0.2)'
      }}>
      <div style={{ marginBottom: '10px', borderBottom: '1px solid #ccc', paddingBottom: '5px' }}>
        <strong>Cache Buster Debug Panel</strong>
        <button onClick={() => setVisible(false)} style={{ float: 'right', border: 'none', background: 'transparent', cursor: 'pointer' }}>
          ✕
        </button>
        <button onClick={checkVersionInfo} style={{ float: 'right', marginRight: '10px', border: 'none', background: 'transparent', cursor: 'pointer' }}>
          🔄
        </button>
      </div>

      <div style={{ marginBottom: '10px' }}>
        <strong>Version Match:</strong>
        <span style={{ color: match ? 'green' : 'red', fontWeight: 'bold' }}>{match ? 'YES ✓' : 'NO ✗'}</span>
      </div>

      <div style={{ marginBottom: '5px' }}>
        <strong>CacheBuster Mounted:</strong> {versionInfo.cacheBusterMounted ? 'Yes ✓' : 'No ✗'}
      </div>

      <div style={{ marginBottom: '5px' }}>
        <strong>Just Updated Flag:</strong> {versionInfo.client.justUpdated}
      </div>

      <div style={{ marginBottom: '10px' }}>
        <strong>Last Checked:</strong> {versionInfo.lastChecked || 'never'}
      </div>

      <div style={{ marginBottom: '10px' }}>
        <strong>Server Version:</strong>
        <pre style={{ margin: '5px 0', padding: '5px', background: '#fff', border: '1px solid #ddd', overflow: 'auto' }}>{JSON.stringify(versionInfo.server, null, 2)}</pre>
      </div>

      <div>
        <strong>Client Version:</strong>
        <pre style={{ margin: '5px 0', padding: '5px', background: '#fff', border: '1px solid #ddd', overflow: 'auto' }}>{JSON.stringify(versionInfo.client, null, 2)}</pre>
      </div>

      <div style={{ marginTop: '10px', borderTop: '1px solid #ccc', paddingTop: '5px' }}>
        <button
          onClick={() => {
            localStorage.removeItem('justUpdated');
            checkVersionInfo();
          }}
          style={{ padding: '5px', marginRight: '5px' }}>
          Reset Just Updated
        </button>

        <button
          onClick={() => {
            localStorage.removeItem('appVersion');
            localStorage.removeItem('appBuildId');
            checkVersionInfo();
          }}
          style={{ padding: '5px', marginRight: '5px' }}>
          Clear Version
        </button>

        <button
          onClick={() => {
            const win = window as any;
            if (win.manualCacheBust) {
              win.manualCacheBust();
            } else {
              alert('manualCacheBust function not available');
            }
          }}
          style={{ padding: '5px' }}>
          Force Refresh
        </button>
      </div>
    </div>
  );
};
